import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { SelectItem } from 'primereact/selectitem';
import { Tag } from 'primereact/tag';
import { ToggleButton } from 'primereact/togglebutton';
import { useTranslation } from 'react-i18next';
import NetworkContainer from '../../components/NetworkContainer';
import ProductTable from '../../components/ProductGrid';
import SearchInput from '../../components/SearchInput';
import { HttpRequestStatus } from '../../components/types/types';
import ViewHeader from '../../components/ViewHeader';
import {
  PRODUCT_LIFECYCLE_ACTIVE,
  PRODUCT_LIFECYCLE_DISCONTINUED,
  PRODUCT_LIFECYCLE_PENDING,
  PRODUCT_LIFECYCLE_WAITING_FOR_SELECTION,
  PRODUCT_STATUS_PRE_PRODUCT,
  PRODUCT_STATUS_PRODUCTIZED,
} from '../../constants/pim-attribute-options';
import useFileDownload from '../../hooks/useFileDownload';
import useProducts from '../../hooks/useProducts';
import './Products.scss';

const LIFECYCLE_OPTIONS = [
  PRODUCT_LIFECYCLE_ACTIVE,
  PRODUCT_LIFECYCLE_DISCONTINUED,
  PRODUCT_LIFECYCLE_WAITING_FOR_SELECTION,
  PRODUCT_LIFECYCLE_PENDING,
];

const STATUS_OPTIONS = [
  PRODUCT_STATUS_PRE_PRODUCT,
  PRODUCT_STATUS_PRODUCTIZED,
];

export default function Products() {
  const { t } = useTranslation();
  const {
    result,
    status,
    pageState,
    keyword,
    hasPendingChanges,
    isDolly,
    onlyDollyPossibilities,
    isFetching,
    productNumber,
    productStatus,
    productLifecycle,
    onChangeStatus,
    onChangePage,
    onChangeKeyword,
    onChangePendingChangeFilter,
    onChangeDollyFilter,
    onChangeOnlyDollyPossibilities,
    onChangeProductNumber,
    onChangeLifecycle,
    handleClearFilter,
  } = useProducts();

  const handleDownloadResponse = (file: File) => {
    const blob = new Blob([file]);
    const tempLink = document.createElement('a');
    tempLink.href = window.URL.createObjectURL(blob);
    tempLink.download = `products_spa_${new Date().toISOString().slice(0, 10)}.xlsx`;
    tempLink.click();
  };

  const lifeCycleDropdownItemTemplate = (option: SelectItem) => {
    const tag = option.value.toLowerCase();
    return (
      <Tag
        className={`p-tag-product-lifecycle-${tag}`}
        value={t(`products.label-product-lifecycle-${tag}`)}
      />
    );
  };

  const statusDropdownItemTemplate = (option: SelectItem) => {
    const tag = option.value.toLowerCase();
    return (
      <Tag
        className={`p-tag-product-status-${tag}`}
        value={t(`products.label-product-status-${tag}`)}
      />
    );
  };

  const { onDownload, downloadStatus } = useFileDownload({
    callback: handleDownloadResponse,
    url: '/product-export',
  });

  const lifecycleOptions = LIFECYCLE_OPTIONS.map((value) => ({
    label: t(`products.label-product-lifecycle-${value.toLocaleLowerCase()}`), value,
  }));

  const statusOptions = STATUS_OPTIONS.map((value) => ({
    label: t(`products.label-product-status-${value.toLocaleLowerCase()}`), value,
  }));

  return (
    <NetworkContainer status={status as HttpRequestStatus}>
      <ViewHeader
        title={t('products.title-product-management')}
        description={t('products.label-product-management-description')}
      >
        <Button
          label={t('common.cmd-export')}
          icon="pi pi-file-export"
          size="small"
          onClick={onDownload}
          disabled={downloadStatus.isDownloading}
        />
      </ViewHeader>
      <div className="field flex gap-2">
        <div style={{ minWidth: '15rem' }}>
          <SearchInput
            placeholder={t('products.label-product-number')}
            onChange={onChangeProductNumber}
            value={productNumber}
            loading={isFetching}
          />
        </div>
        <SearchInput
          placeholder={t('products.label-search-products')}
          onChange={onChangeKeyword}
          value={keyword}
          loading={isFetching}
        />

        <Button
          onClick={handleClearFilter}
          severity="secondary"
          icon="pi pi-times"
        />
      </div>
      <div className="field flex flex-wrap gap-2">
        <ToggleButton
          onLabel={t('products.label-pending-changes')}
          offLabel={t('products.label-pending-changes')}
          onIcon="pi pi-check"
          offIcon="pi pi-times"
          checked={hasPendingChanges}
          onChange={onChangePendingChangeFilter}
          style={{ minWidth: '15rem', height: '45px' }}
        />
        <ToggleButton
          onLabel={t('products.label-only-dollies')}
          offLabel={t('products.label-only-dollies')}
          onIcon="pi pi-check"
          offIcon="pi pi-times"
          checked={isDolly}
          onChange={onChangeDollyFilter}
          style={{ minWidth: '10rem', height: '45px' }}
        />
        <ToggleButton
          onLabel={t('products.label-only-dolly-possibilities')}
          offLabel={t('products.label-only-dolly-possibilities')}
          onIcon="pi pi-check"
          offIcon="pi pi-times"
          checked={onlyDollyPossibilities}
          onChange={onChangeOnlyDollyPossibilities}
          style={{ minWidth: '15rem', height: '45px' }}
        />
        <Dropdown
          data-testid="lifecycle-filter"
          onChange={onChangeLifecycle}
          placeholder={t('common.label-lifecycle')}
          className="filter-dropdown"
          itemTemplate={lifeCycleDropdownItemTemplate}
          options={lifecycleOptions}
          showClear
          value={productLifecycle}
        />
        <Dropdown
          data-testid="status-filter"
          onChange={onChangeStatus}
          placeholder={t('common.label-status')}
          className="filter-dropdown"
          itemTemplate={statusDropdownItemTemplate}
          options={statusOptions}
          showClear
          value={productStatus}
        />
      </div>
      <ProductTable
        items={result?.content || []}
        totalRecords={result?.totalElements}
        loading={isFetching}
        pageState={pageState}
        onPageChange={onChangePage}
      />
    </NetworkContainer>
  );
}
